
import { currency } from '../../utils/currency';
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, message, Modal, Spin, Alert, Typography, Segmented } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Liability, LiabilitiesResponse, Asset } from '../../types';
import LiabilityDetails from './liabilitydetail';
import AddEditLiabilityForm from './liabilityform';
import { useGlobalContext } from '../../store/GlobalContext';
import { LiabilityService } from './liability.service';

const LiabilitiesListPage: React.FC = () => {
  const [status, setStatus] = useState<'open' | 'closed'>('open');
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [totalLiabilities, setTotalLiabilities] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedLiability, setSelectedLiability] = useState<Liability | null>(null);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState<boolean>(false);
  const [isFormVisible, setIsFormVisible] = useState<boolean>(false);
  const [formLoading, setFormLoading] = useState<boolean>(false);

  const { selectedEstate } = useGlobalContext();
  const estateId = selectedEstate?.uuid || '';

  // Fetch liabilities when component mounts or pagination changes
  useEffect(() => {
    if (estateId) {
      fetchLiabilities(currentPage, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, estateId, status]);

  const fetchLiabilities = async (page: number, limit: number) => {
    setLoading(true);
    setError('');
    try {
      const response: LiabilitiesResponse = await LiabilityService.fetchLiabilities({ estateId, page, limit, status });
      setLiabilities(response.data);
      setTotalLiabilities(response.total);
      setCurrentPage(response.page);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to fetch liabilities.');
    } finally {
      setLoading(false);
    }
  };

  const showLiabilityDetails = (liability: Liability) => {
    setSelectedLiability(liability);
    setIsDetailsModalVisible(true);
  };

  const closeLiabilityDetails = () => {
    setSelectedLiability(null);
    setIsDetailsModalVisible(false);
  };

  const showAddForm = () => {
    setSelectedLiability(null);
    setIsFormVisible(true);
  };

  const closeAddEditForm = () => {
    setSelectedLiability(null);
    setIsFormVisible(false);
  };

  const handleAddEditLiability = async (liability: Liability) => {
    setFormLoading(true);
    try {
      if (liability.uid) {
        // Update existing liability

        const {estateId, uid, ...liabilityData } = liability;
        await LiabilityService.updateLiability(estateId, uid, liabilityData);
        message.success('Liability updated successfully.');
      } else {
        // Create new liability
        await LiabilityService.createLiability(estateId, liability);
        message.success('Liability added successfully.');
      }
      fetchLiabilities(currentPage, pageSize);
      closeAddEditForm();
    } catch (err: any) {
      console.error(err);
      message.error(err.message || 'Failed to save liability.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteLiability = async (liabilityId: string) => {
    try {
      await LiabilityService.deleteLiability(estateId, liabilityId);
      message.success('Liability deleted successfully.');
      fetchLiabilities(currentPage, pageSize);
      closeLiabilityDetails();
    } catch (err: any) {
      console.error(err);
      message.error(err.message || 'Failed to delete liability.');
    }
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const columns = [
    { title: 'Description', dataIndex: 'description', key: 'description', width: 160, fixed: 'left' as const,
      render: (description: string, record: Liability) => <Button type="link" style={{ whiteSpace: 'normal', height: 'auto', padding: 0, textAlign: 'left' }} onClick={() => showLiabilityDetails(record)}>{description || record.code || 'Unnamed liability'}</Button> },
    { title: 'Unsecured claim', dataIndex: 'unsecuredAmount', key: 'unsecuredAmount', render: (value: number | null) => currency(value) },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Outstanding balance', dataIndex: 'outstandingBalance', key: 'outstandingBalance', render: (value: number | null) => currency(value) },

    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 90,
    },
    {
      title: 'Asset',
      dataIndex: 'asset',
      key: 'assetName',
      // A liability need not be secured on anything, so the asset is nullable.
      render: (asset: Asset | null) => asset?.name ?? '',
    },
    {
      title: 'Monthly Payment',
      dataIndex: 'amount',
      key: 'monthlyPayment',
      className: 'financial-value', render: (value: number) => currency(value),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
    },
    {
      title: 'Date Added',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : ''),
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : ''),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Liability) => (
        <Button type="link" onClick={() => showLiabilityDetails(record)}>
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div className="record-page" style={{ padding: '24px' }}>
      <Typography.Title level={1} style={{ fontSize: 28 }}>Liabilities</Typography.Title>
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Liabilities List</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={showAddForm}>
              Add Liability
            </Button>
          </div>
        }
        style={{ marginTop: '24px' }}
        extra={null}
      >
        <Typography >
          View and manage all liabilities associated with your estate.
        </Typography>
        {error && (
          <Alert
            message={error} action={<Button onClick={() => fetchLiabilities(currentPage, pageSize)}>Retry</Button>}
            type="error"
            showIcon
            style={{ margin: '16px 0' }}
          />
        )}
        <Segmented value={status} options={[{value:'open', label:'Open liabilities'}, {value:'closed', label:'Closed / court accounting file'}]} onChange={value => { setStatus(value as 'open' | 'closed'); setCurrentPage(1); }} style={{margin: '16px 0'}} />
        <p className="table-scroll-hint">Scroll within the table for more columns.</p>
        {!error && <Spin spinning={loading}>
          <Table
            locale={{ emptyText: loading ? 'Loading records...' : 'No records found for this view.' }}
            columns={columns}
            dataSource={loading ? [] : liabilities}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalLiabilities,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20', '50'],
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
          />
        </Spin>}
      </Card>

      {/* Add/Edit Liability Form Modal */}
      <AddEditLiabilityForm
        visible={isFormVisible}
        onClose={closeAddEditForm}
        initialData={selectedLiability || undefined}
        onSubmit={handleAddEditLiability}
        estateId={estateId}
      />

      {/* Liability Details Modal */}
      <LiabilityDetails
        visible={isDetailsModalVisible}
        onClose={closeLiabilityDetails}
        liability={selectedLiability}
        onEdit={(liability: Liability) => {
          setSelectedLiability(liability);
          setIsFormVisible(true);
        }}
        onDelete={handleDeleteLiability}
      />
    </div>
  );
};

export default LiabilitiesListPage;
